import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { visitsApi, scheduleApi } from '../services/api';
import { formatTimeCT, formatTimeCTInput, ctInputToUTC, formatDateLabel, localTZAbbr } from '../utils/timezone';
import type { VisitWithStore } from 'shared';

interface Props {
  visit: VisitWithStore;
  onClose: () => void;
}

type RemovePhase = 'idle' | 'loading' | 'ready' | 'picking';

const VISIT_TYPE_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'training', label: 'Training' },
  { value: 'post_market', label: 'Post-Market' },
] as const;

export function VisitPopup({ visit, onClose }: Props) {
  const queryClient = useQueryClient();
  const date = visit.start_datetime.split('T')[0];

  const [startTimeCT, setStartTimeCT] = useState(formatTimeCTInput(visit.start_datetime));
  const [duration, setDuration] = useState(visit.duration_minutes);
  const [visitType, setVisitType] = useState(visit.visit_type);
  const [notes, setNotes] = useState(visit.notes ?? '');

  const [moveMode, setMoveMode] = useState(false);
  const [moveDate, setMoveDate] = useState(() => {
    const d = new Date(date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  });

  const [removePhase, setRemovePhase] = useState<RemovePhase>('idle');
  const [suggestedDate, setSuggestedDate] = useState<string | null>(null);
  const [pickedDate, setPickedDate] = useState(() => {
    const d = new Date(date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  });

  const isDirty =
    startTimeCT !== formatTimeCTInput(visit.start_datetime) ||
    duration !== visit.duration_minutes ||
    visitType !== visit.visit_type ||
    notes !== (visit.notes ?? '');

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof visitsApi.update>[1]) => visitsApi.update(visit.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => visitsApi.delete(visit.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });

  function handleSave() {
    updateMutation.mutate({
      start_datetime: ctInputToUTC(date, startTimeCT),
      duration_minutes: duration,
      visit_type: visitType,
      notes: notes || undefined,
    });
  }

  function handleMove() {
    updateMutation.mutate({
      start_datetime: ctInputToUTC(moveDate, startTimeCT),
      duration_minutes: duration,
    });
  }

  async function initiateRemove() {
    setRemovePhase('loading');
    const nextDay = new Date(date + 'T00:00:00Z');
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    const fromDate = nextDay.toISOString().slice(0, 10);
    try {
      const result = await scheduleApi.slotStore(visit.store_id, fromDate);
      setSuggestedDate(result.scheduled_date);
      setRemovePhase('ready');
    } catch {
      setSuggestedDate(null);
      setRemovePhase('ready');
    }
  }

  async function confirmRemove(rescheduleFrom: string | null) {
    await deleteMutation.mutateAsync();
    if (rescheduleFrom) {
      try {
        await scheduleApi.slotStore(visit.store_id, rescheduleFrom, visit.notes ?? undefined);
      } catch {
        // reschedule failed — that's OK
      }
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
    }
    onClose();
  }

  const endPreview = (() => {
    const [h, m] = startTimeCT.split(':').map(Number);
    const total = h * 60 + m + duration;
    const nh = Math.min(Math.floor(total / 60), 23);
    const nm = total % 60;
    const period = nh >= 12 ? 'PM' : 'AM';
    const h12 = nh % 12 || 12;
    return `${h12}:${String(nm).padStart(2, '0')} ${period}`;
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{ position: 'relative', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>{visit.store.name}</h3>
            {visit.store.chain_name && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{visit.store.chain_name}</div>}
            {visit.store.address_line1 && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {visit.store.address_line1}{visit.store.city ? `, ${visit.store.city}` : ''}{visit.store.state ? `, ${visit.store.state}` : ''}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
              {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
        </div>

        {/* Edit fields */}
        {!moveMode && removePhase === 'idle' && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px', fontWeight: 500 }}>Start time ({localTZAbbr()})</label>
                <input type="time" value={startTimeCT}
                  onChange={(e) => setStartTimeCT(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px', fontWeight: 500 }}>Duration (min)</label>
                <input type="number" value={duration} min={15} step={15}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 15)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Ends at <strong>{endPreview}</strong> · Originally {formatTimeCT(visit.start_datetime)} – {formatTimeCT(visit.end_datetime)}
            </div>

            <div>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px', fontWeight: 500 }}>Visit type</label>
              <select value={visitType} onChange={(e) => setVisitType(e.target.value as typeof visitType)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }}>
                {VISIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px', fontWeight: 500 }}>Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Add notes…"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>
          </div>
        )}

        {/* Move mode */}
        {moveMode && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#374151', fontWeight: 500 }}>Move this visit to another day</p>
            <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>The visit will keep the same start time ({formatTimeCT(visit.start_datetime)}) on the new date.</p>
            <div>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px', fontWeight: 500 }}>New date</label>
              <input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleMove} disabled={updateMutation.isPending}
                style={{ flex: 1, padding: '10px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                {updateMutation.isPending ? 'Moving…' : 'Move Visit'}
              </button>
              <button onClick={() => setMoveMode(false)}
                style={{ padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>
                Back
              </button>
            </div>
          </div>
        )}

        {/* Remove flow */}
        {removePhase !== 'idle' && !moveMode && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#92400e' }}>Remove this visit?</p>
            {removePhase === 'loading' && <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Finding next available slot…</p>}
            {(removePhase === 'ready' || removePhase === 'picking') && (
              <>
                {suggestedDate && removePhase === 'ready' && (
                  <button onClick={() => confirmRemove(suggestedDate)}
                    style={{ padding: '10px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, textAlign: 'left' }}>
                    Reschedule to {formatDateLabel(suggestedDate)} (suggested)
                  </button>
                )}
                {removePhase === 'ready' && (
                  <button onClick={() => setRemovePhase('picking')}
                    style={{ padding: '10px 14px', background: '#eff6ff', color: '#2563eb', border: '1px solid #93c5fd', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, textAlign: 'left' }}>
                    Pick a different date
                  </button>
                )}
                {removePhase === 'picking' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Schedule from this date onward:</label>
                    <input type="date" value={pickedDate} onChange={(e) => setPickedDate(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <button onClick={() => confirmRemove(pickedDate)}
                      style={{ padding: '10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
                      Reschedule
                    </button>
                  </div>
                )}
                <button onClick={() => confirmRemove(null)}
                  style={{ padding: '10px 14px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', textAlign: 'left' }}>
                  Just remove — I'll reschedule manually
                </button>
                <button onClick={() => setRemovePhase('idle')}
                  style={{ padding: '4px 8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#9ca3af' }}>
                  Cancel
                </button>
              </>
            )}
          </div>
        )}

        {/* Footer actions */}
        {!moveMode && removePhase === 'idle' && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {isDirty && (
              <button onClick={handleSave} disabled={updateMutation.isPending}
                style={{ flex: 1, padding: '10px', backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, minWidth: '100px' }}>
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </button>
            )}
            <button onClick={() => setMoveMode(true)}
              style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: 500 }}>
              Move Day
            </button>
            <button onClick={initiateRemove}
              style={{ padding: '10px 14px', border: '1px solid #fca5a5', borderRadius: '6px', background: '#fef2f2', cursor: 'pointer', fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
